import {
  CHEQUE_LEAF_STATUS_LABELS,
  type ChequeBook,
  type ChequeLeafStatus,
  createChequeBookSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  type Tone,
  useToast,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookCopy, Plus } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import { SimpleTable, tdClass, thClass } from "../../../features/counterparties/shared.js";
import { useChequeBooks } from "../../../features/instruments/queries.js";
import {
  InstrumentPage,
  useInstrumentPermissions,
  useRefreshInstruments,
} from "../../../features/instruments/shared.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { api, ApiError } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/cek-senet/karneler")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: ChequeBooksPage,
});

const LEAF_TONE: Record<ChequeLeafStatus, Tone> = {
  UNUSED: "neutral",
  USED: "info",
  CANCELLED: "warning",
  LOST: "danger",
};

function ChequeBooksPage() {
  const { t } = useTranslation();
  const canManage = useInstrumentPermissions().canManageBooks;
  const [creating, setCreating] = useState(false);
  return (
    <InstrumentPage
      title={t("menu.instrumentBooks")}
      description={t("instruments.books.description")}
      actions={
        canManage ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("instruments.books.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <BooksBody companyId={companyId} creating={creating} onCreatingChange={setCreating} />
      )}
    </InstrumentPage>
  );
}

function BooksBody({
  companyId,
  creating,
  onCreatingChange,
}: {
  companyId: string;
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const books = useChequeBooks(companyId, true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = (books.data ?? []).find((book) => book.id === selectedId) ?? books.data?.[0] ?? null;
  const headingId = useId();

  if (books.isPending) return <SkeletonRows rows={5} />;
  if (books.error) return <Alert tone="danger">{errorMessage(books.error)}</Alert>;

  return (
    <div className="grid gap-4">
      {(books.data ?? []).length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<BookCopy />}
          title={t("instruments.books.emptyTitle")}
          description={t("instruments.books.emptyDescription")}
        />
      ) : (
        <section aria-labelledby={headingId} className="grid gap-2">
          <h2 id={headingId} className="text-base font-semibold">
            {t("instruments.books.listTitle")}
          </h2>
          <SimpleTable
            caption={t("instruments.books.listTitle")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("instruments.books.serialRange")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.fields.bankAccount")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.books.receivedOn")}
                </th>
                <th scope="col" className={thClass}>
                  {t("instruments.books.counts")}
                </th>
                <th scope="col" className={thClass}>
                  {t("common.actions")}
                </th>
              </tr>
            }
          >
            {(books.data ?? []).map((book) => (
              <tr key={book.id} className="border-t">
                <td className={`${tdClass} font-mono text-xs font-semibold`}>
                  {book.firstSerial} – {book.lastSerial}
                  {book.isActive ? null : (
                    <StatusBadge tone="neutral" className="ml-2">
                      {t("common.inactive")}
                    </StatusBadge>
                  )}
                </td>
                <td className={tdClass}>{book.bankAccountName}</td>
                <td className={`${tdClass} tabular`}>{formatDate(book.receivedOn)}</td>
                <td className={tdClass}>
                  <span className="flex flex-wrap gap-1.5">
                    {(Object.keys(book.counts) as ChequeLeafStatus[]).map((status) => (
                      <StatusBadge key={status} tone={LEAF_TONE[status]}>
                        {CHEQUE_LEAF_STATUS_LABELS[status]}: {book.counts[status]}
                      </StatusBadge>
                    ))}
                  </span>
                </td>
                <td className={tdClass}>
                  <Button
                    variant={selected?.id === book.id ? "secondary" : "ghost"}
                    size="sm"
                    aria-pressed={selected?.id === book.id}
                    onClick={() => setSelectedId(book.id)}
                  >
                    {t("instruments.books.showLeaves")}
                  </Button>
                </td>
              </tr>
            ))}
          </SimpleTable>
        </section>
      )}

      {selected ? <LeavesSection companyId={companyId} book={selected} /> : null}

      <CreateBookDialog
        companyId={companyId}
        open={creating}
        onOpenChange={onCreatingChange}
        onCreated={(book) => setSelectedId(book.id)}
      />
    </div>
  );
}

function LeavesSection({ companyId, book }: { companyId: string; book: ChequeBook }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const canManage = useInstrumentPermissions().canManageBooks;
  const [changing, setChanging] = useState<{ id: string; serialNo: string } | null>(null);
  const leaves = useQuery({
    queryKey: queryKeys.instruments(companyId, "leaves", book.id),
    queryFn: async () =>
      (await api.GET("/cheque-books/{id}/leaves", { params: { path: { id: book.id } } })).data!.items,
  });

  return (
    <section aria-labelledby={headingId} className="grid gap-2">
      <h2 id={headingId} className="text-base font-semibold">
        {t("instruments.books.leavesTitle", { range: `${book.firstSerial} – ${book.lastSerial}` })}
      </h2>
      {leaves.error ? <Alert tone="danger">{errorMessage(leaves.error)}</Alert> : null}
      {leaves.isPending ? (
        <SkeletonRows rows={4} />
      ) : (
        <SimpleTable
          caption={t("instruments.books.leavesTitle", { range: `${book.firstSerial} – ${book.lastSerial}` })}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("instruments.fields.serialNo")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.books.leafInstrument")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.notes")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {(leaves.data ?? []).map((leaf) => (
            <tr key={leaf.id} className="border-t">
              <td className={`${tdClass} font-mono text-xs font-semibold`}>{leaf.serialNo}</td>
              <td className={tdClass}>
                <StatusBadge tone={LEAF_TONE[leaf.status]}>
                  {CHEQUE_LEAF_STATUS_LABELS[leaf.status]}
                </StatusBadge>
              </td>
              <td className={tdClass}>
                {leaf.instrumentId ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/cek-senet/$id"
                      params={{ id: leaf.instrumentId }}
                      className="font-mono text-xs font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      {leaf.instrumentPortfolioNo}
                    </Link>
                    {leaf.instrumentAmount ? <MoneyText value={leaf.instrumentAmount} /> : null}
                    {leaf.instrumentDueDate ? (
                      <span className="tabular text-xs text-muted-foreground">
                        {formatDate(leaf.instrumentDueDate)}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className={tdClass}>{leaf.note ?? "—"}</td>
              <td className={tdClass}>
                {canManage && leaf.status === "UNUSED" ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChanging({ id: leaf.id, serialNo: leaf.serialNo })}
                  >
                    {t("instruments.books.changeLeaf")}
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}
      <LeafStatusDialog companyId={companyId} leaf={changing} onClose={() => setChanging(null)} />
    </section>
  );
}

/** Kullanılmamış yaprağı iptal / kayıp işaretleme: not ZORUNLU (denetim izi), geri alınamaz. */
function LeafStatusDialog({
  companyId,
  leaf,
  onClose,
}: {
  companyId: string;
  leaf: { id: string; serialNo: string } | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const [status, setStatus] = useState<"CANCELLED" | "LOST">("CANCELLED");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={leaf !== null}
      onOpenChange={(open) => {
        if (!open && !busy) {
          setError(null);
          setNote("");
          onClose();
        }
      }}
      title={t("instruments.books.changeLeafTitle", { serial: leaf?.serialNo ?? "" })}
      description={t("instruments.books.changeLeafDescription")}
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (note.trim().length < 3) return setError(t("instruments.books.noteRequired"));
          setBusy(true);
          setError(null);
          api
            .POST("/cheque-leaves/{id}/status", {
              params: { path: { id: leaf!.id } },
              body: { status, note: note.trim() },
            })
            .then(async () => {
              await refresh(companyId);
              toast({ tone: "success", title: t("instruments.books.leafChanged") });
              setNote("");
              onClose();
            })
            .catch((caught: unknown) => setError(errorMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("instruments.fields.status")} required>
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) => setStatus(event.target.value as "CANCELLED" | "LOST")}
            >
              <option value="CANCELLED">{CHEQUE_LEAF_STATUS_LABELS.CANCELLED}</option>
              <option value="LOST">{CHEQUE_LEAF_STATUS_LABELS.LOST}</option>
            </Select>
          )}
        </FormField>
        <FormField label={t("instruments.fields.notes")} required>
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CreateBookDialog({
  companyId,
  open,
  onOpenChange,
  onCreated,
}: {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (book: ChequeBook) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const accounts = useBankAccounts(companyId);
  const [bankAccountId, setBankAccountId] = useState("");
  const [serialPrefix, setSerialPrefix] = useState("");
  const [firstNo, setFirstNo] = useState("");
  const [lastNo, setLastNo] = useState("");
  const [digits, setDigits] = useState("7");
  const [receivedOn, setReceivedOn] = useState<string | null>(todayIstanbul());
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    const parsed = createChequeBookSchema.safeParse({
      companyId,
      bankAccountId,
      serialPrefix,
      firstNo: Number(firstNo),
      lastNo: Number(lastNo),
      digits: Number(digits),
      receivedOn: receivedOn ?? "",
      notes: notes.trim() || null,
    });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message])),
      );
      setError(t("instruments.books.checkFields"));
      setBusy(false);
      return;
    }
    try {
      const book = (await api.POST("/cheque-books", { body: parsed.data })).data!;
      await refresh(companyId);
      toast({
        tone: "success",
        title: t("instruments.books.created", { range: `${book.firstSerial} – ${book.lastSerial}` }),
      });
      onCreated(book);
      onOpenChange(false);
      setFirstNo("");
      setLastNo("");
      setNotes("");
    } catch (caught) {
      if (caught instanceof ApiError && caught.problem.errors)
        setFieldErrors(Object.fromEntries(caught.problem.errors.map((item) => [item.path, item.message])));
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !busy && onOpenChange(next)}
      title={t("instruments.books.create")}
      description={t("instruments.books.createDescription")}
      className="sm:max-w-2xl"
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Card className="grid gap-4 border-0 p-0 shadow-none sm:grid-cols-2">
          <FormField label={t("instruments.fields.bankAccount")} error={fieldErrors.bankAccountId} required>
            {(control) => (
              <Select
                {...control}
                value={bankAccountId}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">{t("common.select")}</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bankName} · {account.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField
            label={t("instruments.books.serialPrefix")}
            description={t("instruments.books.serialPrefixHint")}
            error={fieldErrors.serialPrefix}
          >
            {(control) => (
              <Input
                {...control}
                value={serialPrefix}
                maxLength={6}
                onChange={(event) => setSerialPrefix(event.target.value.toUpperCase())}
              />
            )}
          </FormField>
          <FormField label={t("instruments.books.firstNo")} error={fieldErrors.firstNo} required>
            {(control) => (
              <Input
                {...control}
                value={firstNo}
                inputMode="numeric"
                className="tabular"
                onChange={(event) => setFirstNo(event.target.value.replace(/\D/g, ""))}
              />
            )}
          </FormField>
          <FormField label={t("instruments.books.lastNo")} error={fieldErrors.lastNo} required>
            {(control) => (
              <Input
                {...control}
                value={lastNo}
                inputMode="numeric"
                className="tabular"
                onChange={(event) => setLastNo(event.target.value.replace(/\D/g, ""))}
              />
            )}
          </FormField>
          <FormField
            label={t("instruments.books.digits")}
            description={t("instruments.books.digitsHint")}
            error={fieldErrors.digits}
            required
          >
            {(control) => (
              <Input
                {...control}
                value={digits}
                inputMode="numeric"
                className="tabular"
                onChange={(event) => setDigits(event.target.value.replace(/\D/g, ""))}
              />
            )}
          </FormField>
          <FormField label={t("instruments.books.receivedOn")} error={fieldErrors.receivedOn} required>
            {(control) => <DateInput {...control} value={receivedOn} onValueChange={setReceivedOn} />}
          </FormField>
          <FormField
            label={t("instruments.fields.notes")}
            error={fieldErrors.notes}
            className="sm:col-span-2"
          >
            {(control) => (
              <Input
                {...control}
                value={notes}
                maxLength={500}
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </FormField>
        </Card>
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("instruments.books.createSubmit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
